import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Container,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import axios from '../../utils/axios';

const AdminSignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the redirect URL from query parameters if it exists
  const params = new URLSearchParams(location.search);
  const redirectUrl = params.get('redirect') || '/admin/dashboard';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Anti-brute force variables
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutEndTime, setLockoutEndTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);

  // Check if already authenticated and redirect
  useEffect(() => {
    const adminId = localStorage.getItem('adminId');
    if (adminId) {
      navigate(redirectUrl);
    }
  }, [navigate, redirectUrl]);

  // Check for existing lockout in localStorage
  useEffect(() => {
    const storedLockoutEndTime = localStorage.getItem('adminLockoutEndTime');
    const storedLoginAttempts = localStorage.getItem('adminLoginAttempts');
    
    if (storedLockoutEndTime) {
      const endTime = parseInt(storedLockoutEndTime);
      if (endTime > Date.now()) {
        setIsLockedOut(true);
        setLockoutEndTime(endTime);
        setRemainingTime(Math.ceil((endTime - Date.now()) / 1000));
      } else {
        // Lockout period has expired
        localStorage.removeItem('adminLockoutEndTime');
        localStorage.removeItem('adminLoginAttempts');
      }
    } else if (storedLoginAttempts) {
      setLoginAttempts(parseInt(storedLoginAttempts));
    }
  }, []);

  // Countdown timer for lockout period
  useEffect(() => {
    let interval = null;
    
    if (isLockedOut && remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime((prevTime) => {
          const newTime = prevTime - 1;
          if (newTime <= 0) {
            clearInterval(interval);
            setIsLockedOut(false);
            setLoginAttempts(0);
            localStorage.removeItem('adminLockoutEndTime');
            localStorage.removeItem('adminLoginAttempts');
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLockedOut, remainingTime]);

  const formatRemainingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleLoginFailure = () => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);
    localStorage.setItem('adminLoginAttempts', newAttempts);
    
    // Lock out after 5 failed attempts
    if (newAttempts >= 5) {
      const lockoutDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
      const endTime = Date.now() + lockoutDuration;
      
      setIsLockedOut(true);
      setLockoutEndTime(endTime);
      setRemainingTime(lockoutDuration / 1000);
      
      localStorage.setItem('adminLockoutEndTime', endTime.toString());
      setError('Too many failed login attempts. Account locked for 5 minutes.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if locked out
    if (isLockedOut) {
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      // This is just a placeholder for the API call
      // Replace with your actual API endpoint
      const response = await axios.post('/api/admin/signin/', {
        email: formData.email,
        password: formData.password,
        remember_me: rememberMe
      });
      
      // Reset login attempts on successful login
      setLoginAttempts(0);
      localStorage.removeItem('adminLoginAttempts');
      localStorage.removeItem('adminLockoutEndTime');
      
      // Store authentication data
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('adminId', response.data.admin_id);
      localStorage.setItem('adminRole', response.data.role); // 'master', 'co', or 'sub'
      localStorage.setItem('userType', 'admin');
      
      if (response.data.school_id) {
        localStorage.setItem('schoolId', response.data.school_id);
      }
      
      // Redirect to admin dashboard
      navigate(redirectUrl);
      
    } catch (error) {
      handleLoginFailure();
      setError(error.response?.data?.error || 'Sign in failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ 
        mt: 8,
        mb: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <AdminPanelSettingsIcon sx={{ fontSize: 64, color: '#DEA514', mb: 2 }} />
          <Typography component="h1" variant="h5" gutterBottom>
            Administrator Sign In
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please enter your admin credentials
          </Typography>
          
          {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
          
          {isLockedOut && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Account temporarily locked. Please try again in {formatRemainingTime(remainingTime)}.
            </Alert>
          )}
          
          {!isLockedOut && loginAttempts > 0 && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              Failed login attempts: {loginAttempts}/5
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading || isLockedOut}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading || isLockedOut}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          disabled={isLockedOut}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      color="primary"
                      disabled={loading || isLockedOut}
                    />
                  }
                  label="Remember me"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || isLockedOut}
                  sx={{
                    mt: 2,
                    mb: 2,
                    bgcolor: '#DEA514',
                    '&:hover': { bgcolor: '#B88A10' },
                    height: '48px'
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminSignIn;
